import { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Icon, Loading, ButtonTooltip } from '@evertrue/et-components';
import {
  walkCadenceGraph,
  getStepIcon,
  isManualStep,
  getStepBadgeColor,
  getStepModeSubline,
  getBranchStepLabelList,
} from '../../utils/cadence-step-utils';
import StepLabelBlock from '../shared/step-label-block';
// Re-export for consumers that import these from this module
export { isManualStep, getStepBadgeColor };

const ALL_METRIC_COLUMNS = [
  {
    field: 'taskCreated',
    label: 'TASK CREATED',
    tooltip: 'Manual tasks that were created and assigned to a fundraiser for this step.',
    manualOnly: true,
  },
  {
    field: 'taskComplete',
    label: 'TASK COMPLETE',
    tooltip: 'Tasks that were manually completed by a fundraiser for this step.',
    manualOnly: true,
  },
  {
    field: 'sends',
    label: 'SENT',
    tooltip:
      'Total constituents who were sent this step. Sent = Delivered + Error. Skipped constituents are NOT counted here.',
    emailOnly: true,
  },
  {
    field: 'delivered',
    label: 'DELIVERED',
    tooltip: 'Emails that successfully reached the recipient\u2019s inbox.',
    emailOnly: true,
  },
  {
    field: 'opens',
    label: 'OPENED',
    tooltip: 'Number of times the email was opened by recipients.',
    emailOnly: true,
  },
  {
    field: 'clicks',
    label: 'CLICKED',
    tooltip: 'Number of times links in the email were clicked by recipients.',
    emailOnly: true,
  },
  {
    field: 'replied',
    label: 'REPLIED',
    tooltip: 'Number of recipients who replied to the email.',
    emailOnly: true,
  },
  {
    field: 'qualified',
    label: 'SUCCESS REACHED',
    tooltip: 'Constituents who reached the defined success criteria for this cadence.',
  },
  {
    field: 'skipped',
    label: 'SKIPPED',
    tooltip:
      'Constituents for whom this step was skipped without a send being attempted. The skip may have been done manually or automatically (e.g. due to past bounce, missing contact info, past unsubscribe, missing merge field).\n\nSkipped constituents may or may not have exited the cadence.',
    isIssue: true,
    issueVariant: 'skipped',
  },
  {
    field: 'errorCount',
    label: 'ERROR',
    tooltip: 'Sends that resulted in a delivery error. Constituents with errors are always exited from the cadence.',
    emailOnly: true,
    isIssue: true,
    issueVariant: 'errors',
    headerColor: 'var(--red, #dc2626)',
  },
  {
    field: 'unsubscribed',
    label: 'UNSUBSCRIBED',
    tooltip:
      'Sends that resulted in the recipient unsubscribing. Constituents who unsubscribe are always removed from the cadence.',
    emailOnly: true,
    headerColor: 'var(--gold, #d97706)',
  },
  {
    field: 'exited',
    label: 'EXITED',
    tooltip:
      'Constituents removed from the cadence at this step. Exits can happen as the result of a manual removal, an error, an unsubscribe, or certain skips.',
    headerColor: '#F43F5E',
  },
];

/** Default visible columns — all shown initially */
const DEFAULT_VISIBLE = Object.fromEntries(ALL_METRIC_COLUMNS.map((c) => [c.field, true]));

const STORAGE_KEY = 'cadence-perf-table-columns';

const loadColumnVisibility = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? { ...DEFAULT_VISIBLE, ...JSON.parse(stored) } : { ...DEFAULT_VISIBLE };
  } catch {
    return { ...DEFAULT_VISIBLE };
  }
};

const ROWS_PER_PAGE = 5;

/** Reason key → user-facing label (skip reasons). */
const SKIP_REASON_LABELS = {
  no_email: 'Missing email address',
  missing_contact_info: 'Missing email address',
  missing_merge_field: 'Missing merge field',
  missing_merge_fields: 'Missing merge field',
  no_phone: 'No phone number',
  unsubscribed: 'Unsubscribed',
  historic_hard_bounce: 'Previous hard bounce',
  bounced_prev: 'Bounced on previous step',
  invalid_number: 'Invalid phone number',
  blocked_sender: 'Blocked by recipient org',
  cadence_qualification: 'Enrolled in another cadence',
  user_skip: 'Manually skipped by user',
  unknown: 'Skipped (reason not specified)',
};

/** Reason key → user-facing label (error reasons). */
const ERROR_REASON_LABELS = {
  hard_bounce: 'Hard bounce',
  soft_bounce: 'Soft bounce',
  dmarc_policy: 'DMARC policy',
  dns_error: 'DNS error',
  spam_block: 'Spam block',
  blocked: 'ISP blocked',
  blocked_sender: 'Sender blocked',
  invalid_number: 'Invalid number',
  unknown: 'Error (reason not specified)',
};

const YES_BRANCH_BORDER = '3px solid #16a34a';
const NO_BRANCH_BORDER = '3px solid #dc2626';

/** Format a numeric value for display, showing "—" for null/undefined. */
export const formatNumber = (val) => {
  if (val === null || val === undefined) return '—';
  return val.toLocaleString();
};

/**
 * Whether a metric column's cell should show "N/A" for a given step.
 *
 * emailOnly columns (SENT/DELIVERED/OPENED/CLICKED/REPLIED) need real email-send data,
 * which exists whenever the step itself is an email step — including a manually-triggered
 * send, which still generates real cadence_activity rows. Gate on step.type, not on whether
 * the send was manual vs. automated (isManualStep), which is an orthogonal concern.
 *
 * manualOnly columns (TASK CREATED/TASK COMPLETE) need manual task-completion data, which
 * only exists for manual steps (isManualStep) — a literal task step, or a manually-sent email.
 */
export const shouldShowNA = (step, col) => {
  const isEmailStep = step.type === 'email';
  return Boolean((col.emailOnly && !isEmailStep) || (col.manualOnly && !isManualStep(step)));
};

/**
 * Breakdown popover for skipped/errors pills.
 * Renders a positioned popover anchored to the pill element.
 */
const BreakdownPopover = ({ reasons, labels, accentColor, onClose, anchorRect }) => {
  const ref = useRef(null);

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [onClose]);

  const entries = Object.entries(reasons).filter(([, count]) => count > 0);
  if (entries.length === 0) return null;

  const style = anchorRect
    ? {
        position: 'fixed',
        top: anchorRect.bottom + 6,
        left: anchorRect.left + anchorRect.width / 2,
        transform: 'translateX(-50%)',
      }
    : {};

  return (
    <div ref={ref} className="issue-breakdown-popover" style={style}>
      <div className="issue-breakdown-popover--header" style={{ color: accentColor }}>
        BREAKDOWN
      </div>
      <div className="issue-breakdown-popover--list">
        {entries.map(([key, count]) => (
          <div key={key} className="issue-breakdown-popover--row">
            <span className="issue-breakdown-popover--label">{labels[key] || key}</span>
            <span className="issue-breakdown-popover--count">{count}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

BreakdownPopover.propTypes = {
  reasons: PropTypes.object.isRequired,
  labels: PropTypes.object.isRequired,
  accentColor: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  anchorRect: PropTypes.object,
};

/**
 * Colored count pill with optional breakdown popover on click.
 */
const IssuePill = ({ count, reasons, labels, variant }) => {
  const [showPopover, setShowPopover] = useState(false);
  const btnRef = useRef(null);
  const [anchorRect, setAnchorRect] = useState(null);

  const updateRect = () => {
    if (btnRef.current) setAnchorRect(btnRef.current.getBoundingClientRect());
  };

  if (!count || count === 0) {
    return <span className="issue-pill is-zero">0</span>;
  }

  const isAmber = variant === 'skipped';
  const pillClass = `issue-pill ${isAmber ? 'is-amber' : 'is-red'}`;
  const accentColor = isAmber ? '#92400E' : '#991B1B';

  return (
    <span className="issue-pill-wrap">
      <button
        ref={btnRef}
        type="button"
        className={pillClass}
        onClick={() => {
          updateRect();
          setShowPopover((v) => !v);
        }}
        onMouseEnter={() => {
          if (reasons) {
            updateRect();
            setShowPopover(true);
          }
        }}
        onMouseLeave={() => setShowPopover(false)}
      >
        {count}
      </button>
      {showPopover && reasons && Object.keys(reasons).length > 0 && (
        <BreakdownPopover
          reasons={reasons}
          labels={labels}
          accentColor={accentColor}
          anchorRect={anchorRect}
          onClose={() => setShowPopover(false)}
        />
      )}
    </span>
  );
};

IssuePill.propTypes = {
  count: PropTypes.number,
  reasons: PropTypes.object,
  labels: PropTypes.object.isRequired,
  variant: PropTypes.oneOf(['skipped', 'errors']).isRequired,
};

/**
 * Format a wait duration object into a readable string.
 */
export const formatWaitDuration = (wait) => {
  if (!wait) return null;
  const parts = [];
  if (wait.days) parts.push(`${wait.days} day${wait.days !== 1 ? 's' : ''}`);
  if (wait.hours) parts.push(`${wait.hours} hour${wait.hours !== 1 ? 's' : ''}`);
  if (wait.minutes) parts.push(`${wait.minutes} minute${wait.minutes !== 1 ? 's' : ''}`);
  return parts.length > 0 ? parts.join(', ') : null;
};

export const getStepSubline = (step) => {
  if (step.type === 'wait') {
    const c = step.eventConfig || {};
    return formatWaitDuration({ days: c.duration_days, hours: c.duration_hours, minutes: c.duration_minutes });
  }
  if (step.type !== 'email' && step.type !== 'task') return null;
  return getStepModeSubline(step);
};

/**
 * Manage Columns popover — checkboxes to toggle metric column visibility.
 */
const ManageColumnsPopover = ({ visibility, onChange, onClose, hasManualStep }) => {
  const ref = useRef(null);
  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [onClose]);

  return (
    <div ref={ref} className="cadence-detail-page--manage-cols-popover">
      <div className="cadence-detail-page--manage-cols-header">Manage Columns</div>
      {ALL_METRIC_COLUMNS.filter((c) => !c.manualOnly || hasManualStep).map((col) => (
        <label key={col.field} className="cadence-detail-page--manage-cols-row">
          <input
            type="checkbox"
            checked={visibility[col.field] !== false}
            onChange={(e) => onChange(col.field, e.target.checked)}
          />
          {col.label}
        </label>
      ))}
    </div>
  );
};

ManageColumnsPopover.propTypes = {
  visibility: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  hasManualStep: PropTypes.bool,
};

const OverallPerformanceTable = ({ steps = [], cadence, loading, error }) => {
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [columnVisibility, setColumnVisibility] = useState(loadColumnVisibility);
  const [showManageCols, setShowManageCols] = useState(false);
  const [expandedBranches, setExpandedBranches] = useState({});

  // Walk the cadence graph to detect branch structure
  const graphItems = useMemo(() => {
    if (!cadence?.events?.length) return null;
    const { items } = walkCadenceGraph(cadence.events, cadence.edges || []);
    return items;
  }, [cadence?.events, cadence?.edges]);

  // Map step ID → step data for quick lookup
  const stepById = useMemo(() => {
    const map = {};
    for (const s of steps) map[String(s.id)] = s;
    return map;
  }, [steps]);

  const toggleBranch = (key) => setExpandedBranches((prev) => ({ ...prev, [key]: !prev[key] }));

  const toggleColumn = useCallback((field, visible) => {
    setColumnVisibility((prev) => {
      const next = { ...prev, [field]: visible };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  const hasManualStep = steps.some((s) => s.type === 'task' || s.isAutomated === false);
  const hasBranches = graphItems && graphItems.some((i) => i.type === 'branch');
  const visibleMetricColumns = ALL_METRIC_COLUMNS.filter(
    (c) => columnVisibility[c.field] !== false && (!c.manualOnly || hasManualStep)
  );

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const sortedSteps = useMemo(() => {
    if (!sortColumn) return steps;
    return [...steps].sort((a, b) => {
      const aVal = a[sortColumn] ?? -1;
      const bVal = b[sortColumn] ?? -1;
      if (typeof aVal === 'string' || typeof bVal === 'string') {
        const cmp = String(aVal).localeCompare(String(bVal));
        return sortDirection === 'asc' ? cmp : -cmp;
      }
      return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    });
  }, [steps, sortColumn, sortDirection]);

  const totalItemCount =
    hasBranches && graphItems
      ? graphItems.filter((i) => i.type === 'step' || i.type === 'branch').length
      : steps.length;
  const totalPages = Math.max(1, Math.ceil(totalItemCount / ROWS_PER_PAGE));
  const startIdx = (currentPage - 1) * ROWS_PER_PAGE;
  const paginatedSteps = sortedSteps.slice(startIdx, startIdx + ROWS_PER_PAGE);

  if (loading) return <Loading />;

  if (error) {
    return <p className="cadence-detail-page--stats-error">Failed to load stats: {error}</p>;
  }

  return (
    <>
      <div className="cadence-detail-page--perf-toolbar">
        <div className="cadence-detail-page--manage-cols-wrap">
          <button
            type="button"
            className="cadence-detail-page--manage-cols-btn"
            onClick={() => setShowManageCols((v) => !v)}
          >
            <Icon icon="settings" size={1} /> Manage Columns
          </button>
          {showManageCols && (
            <ManageColumnsPopover
              visibility={columnVisibility}
              onChange={toggleColumn}
              onClose={() => setShowManageCols(false)}
              hasManualStep={hasManualStep}
            />
          )}
        </div>
      </div>
      <div className="cadence-detail-page--perf-table-wrap">
        <table className="cadence-detail-page--perf-table">
          <thead>
            <tr>
              <th className="cadence-detail-page--perf-th cadence-detail-page--perf-th-step">
                <button type="button" className="cadence-detail-page--sort-btn" onClick={() => handleSort('id')}>
                  STEP <span className="cadence-detail-page--sort-arrow">↕</span>
                </button>
              </th>
              {visibleMetricColumns.map((col) => (
                <th
                  key={col.field}
                  className="cadence-detail-page--perf-th"
                  style={col.headerColor ? { color: col.headerColor } : {}}
                >
                  <button
                    type="button"
                    className="cadence-detail-page--sort-btn"
                    onClick={() => handleSort(col.field)}
                    style={col.headerColor ? { color: 'inherit' } : {}}
                  >
                    <span className={`cadence-detail-page--col-dot cadence-detail-page--col-dot-${col.field}`} />
                    {col.label}
                    {col.tooltip && (
                      <ButtonTooltip
                        contentWidth={240}
                        trigger={
                          <span className="cadence-performance--tooltip-icon-wrap">
                            <Icon icon="info" className="cadence-performance--tooltip-icon" />
                          </span>
                        }
                      >
                        {col.tooltip}
                      </ButtonTooltip>
                    )}
                    <span className="cadence-detail-page--sort-arrow">↕</span>
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {/* Branch separator rows from graph walk */}
            {graphItems &&
              graphItems.some((i) => i.type === 'branch') &&
              (() => {
                // Paginate top-level items (steps + branches each count as 1)
                const topLevelItems = graphItems.filter((i) => i.type === 'step' || i.type === 'branch');
                const pagedItems = topLevelItems.slice(startIdx, startIdx + ROWS_PER_PAGE);
                // Build enriched row list: step rows + branch dividers
                const rows = [];
                for (const item of pagedItems) {
                  if (item.type === 'step') {
                    const step = stepById[String(item.id)];
                    if (step) rows.push({ type: 'step', step, item });
                  }
                  if (item.type === 'branch') {
                    const branchKey = `branch-${item.event?.id}`;
                    // Exclude wait items (no num/suffix) from the step-number labels.
                    const trueNums = getBranchStepLabelList(item.trueBranch);
                    const falseNums = getBranchStepLabelList(item.falseBranch);
                    rows.push({ type: 'branch-header', item, branchKey, trueNums, falseNums });
                    // YES header
                    rows.push({ type: 'branch-yes-header', item, branchKey: `${branchKey}_true`, trueNums });
                    if (expandedBranches[`${branchKey}_true`]) {
                      for (const bs of item.trueBranch) {
                        const step = stepById[String(bs.id)];
                        if (step) rows.push({ type: 'step', step, item: bs, branchSide: 'true' });
                      }
                    }
                    // NO header
                    rows.push({ type: 'branch-no-header', item, branchKey: `${branchKey}_false`, falseNums });
                    if (expandedBranches[`${branchKey}_false`]) {
                      for (const bs of item.falseBranch) {
                        const step = stepById[String(bs.id)];
                        if (step) rows.push({ type: 'step', step, item: bs, branchSide: 'false' });
                      }
                    }
                  }
                }
                return rows.map((row, ri) => {
                  if (row.type === 'branch-header') {
                    return (
                      <tr key={row.branchKey} className="cadence-performance--branch-row">
                        <td colSpan={visibleMetricColumns.length + 1} className="cadence-performance--branch-cell">
                          <button
                            type="button"
                            className="cadence-performance--branch-toggle-btn"
                            onClick={() => {
                              const k = row.branchKey;
                              setExpandedBranches((prev) => {
                                const allOpen = prev[`${k}_true`] && prev[`${k}_false`];
                                return { ...prev, [`${k}_true`]: !allOpen, [`${k}_false`]: !allOpen };
                              });
                            }}
                          >
                            <Icon icon={expandedBranches[`${row.branchKey}_true`] ? 'expand-more' : 'chevron-right'} />
                            <span className="cadence-performance--branch-glyph">&#x2442;</span>
                            <strong>Branch Point:</strong> {row.item.conditionName}
                          </button>
                        </td>
                      </tr>
                    );
                  }
                  if (row.type === 'branch-yes-header') {
                    return (
                      <tr key={row.branchKey} className="cadence-performance--branch-yes-row">
                        <td colSpan={visibleMetricColumns.length + 1} className="cadence-performance--branch-yes-cell">
                          <button
                            type="button"
                            className="cadence-performance--branch-path-btn is-yes"
                            onClick={() => toggleBranch(row.branchKey)}
                          >
                            <Icon icon={expandedBranches[row.branchKey] ? 'expand-more' : 'chevron-right'} />
                            <strong>YES</strong> &ndash; {row.item.condLabels?.yesHeader || 'True path'}: Continue with
                            steps {row.trueNums}
                          </button>
                        </td>
                      </tr>
                    );
                  }
                  if (row.type === 'branch-no-header') {
                    return (
                      <tr key={row.branchKey} className="cadence-performance--branch-no-row">
                        <td colSpan={visibleMetricColumns.length + 1} className="cadence-performance--branch-no-cell">
                          <button
                            type="button"
                            className="cadence-performance--branch-path-btn is-no"
                            onClick={() => toggleBranch(row.branchKey)}
                          >
                            <Icon icon={expandedBranches[row.branchKey] ? 'expand-more' : 'chevron-right'} />
                            <strong>NO</strong> &ndash; {row.item.condLabels?.noHeader || 'False path'}: Continue with
                            steps {row.falseNums}
                          </button>
                        </td>
                      </tr>
                    );
                  }
                  // step row — render same as before
                  const step = row.step;
                  const branchBorder =
                    row.branchSide === 'true'
                      ? YES_BRANCH_BORDER
                      : row.branchSide === 'false'
                        ? NO_BRANCH_BORDER
                        : 'none';
                  const subline = getStepSubline(step);
                  return (
                    <tr
                      key={`${step.id}-${ri}`}
                      className="cadence-detail-page--perf-row"
                      style={row.branchSide ? { borderLeft: branchBorder } : undefined}
                    >
                      <td
                        className="cadence-detail-page--perf-step-cell"
                        style={row.branchSide ? { paddingLeft: 24 } : undefined}
                      >
                        <StepLabelBlock
                          title={step.name && step.name !== step.label ? step.name : undefined}
                          typeLabel={step.label}
                          typeIcon={getStepIcon(step.type, step.eventConfig?.action_type)}
                          badgeColor={getStepBadgeColor(step)}
                          modeSubline={subline}
                        />
                      </td>
                      {visibleMetricColumns.map((col) => {
                        const showNA = shouldShowNA(step, col);
                        if (showNA)
                          return (
                            <td key={col.field} className="cadence-detail-page--perf-val is-na">
                              N/A
                            </td>
                          );
                        const val = step[col.field];
                        return (
                          <td
                            key={col.field}
                            className="cadence-detail-page--perf-val"
                            style={col.headerColor ? { color: col.headerColor } : undefined}
                          >
                            {formatNumber(val)}
                          </td>
                        );
                      })}
                    </tr>
                  );
                });
              })()}
            {/* Fallback: flat step list when no branches */}
            {(!graphItems || !graphItems.some((i) => i.type === 'branch')) &&
              paginatedSteps.map((step, index) => {
                const subline = getStepSubline(step);

                return (
                  <tr key={step.id} className="cadence-detail-page--perf-row">
                    <td className="cadence-detail-page--perf-step-cell">
                      <StepLabelBlock
                        title={step.name && step.name !== step.label ? step.name : undefined}
                        typeLabel={step.label}
                        typeIcon={getStepIcon(step.type, step.eventConfig?.action_type)}
                        badgeColor={getStepBadgeColor(step)}
                        modeSubline={subline}
                      />
                    </td>
                    {visibleMetricColumns.map((col) => {
                      const showNA = shouldShowNA(step, col);

                      if (showNA) {
                        return (
                          <td key={col.field} className="cadence-detail-page--perf-val is-na">
                            N/A
                          </td>
                        );
                      }

                      // Skipped and Error use IssuePill with breakdown popover
                      if (col.isIssue && col.issueVariant === 'skipped') {
                        return (
                          <td
                            key={col.field}
                            className="cadence-detail-page--perf-val cadence-detail-page--perf-issue-cell"
                          >
                            <IssuePill count={step.skipped} labels={SKIP_REASON_LABELS} variant="skipped" />
                          </td>
                        );
                      }
                      if (col.isIssue && col.issueVariant === 'errors') {
                        return (
                          <td
                            key={col.field}
                            className="cadence-detail-page--perf-val cadence-detail-page--perf-issue-cell"
                          >
                            <IssuePill
                              count={step.errorCount}
                              reasons={step.errorReasons}
                              labels={ERROR_REASON_LABELS}
                              variant="errors"
                            />
                          </td>
                        );
                      }

                      return (
                        <td
                          key={col.field}
                          className={`cadence-detail-page--perf-val ${step[col.field] === null || step[col.field] === undefined ? 'is-na' : ''}`}
                        >
                          {formatNumber(step[col.field])}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>

      <div className="cadence-detail-page--perf-pagination">
        <span className="cadence-detail-page--perf-showing">
          {(() => {
            const total =
              hasBranches && graphItems
                ? graphItems.filter((i) => i.type === 'step' || i.type === 'branch').length
                : steps.length;
            return `Showing ${startIdx + 1} to ${Math.min(startIdx + ROWS_PER_PAGE, total)} of ${total} results`;
          })()}
        </span>
        <div className="cadence-detail-page--perf-pages">
          <button
            type="button"
            className="cadence-detail-page--page-btn"
            disabled={currentPage === 1}
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
          >
            Previous
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
            <button
              key={p}
              type="button"
              className={`cadence-detail-page--page-num ${currentPage === p ? 'is-active' : ''}`}
              onClick={() => setCurrentPage(p)}
            >
              {p}
            </button>
          ))}
          <button
            type="button"
            className="cadence-detail-page--page-btn"
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
          >
            Next
          </button>
        </div>
      </div>
    </>
  );
};

OverallPerformanceTable.propTypes = {
  steps: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      type: PropTypes.string,
      label: PropTypes.string,
      isAutomated: PropTypes.bool,
      eventConfig: PropTypes.object,
      emailTemplateId: PropTypes.string,
      sequenceOrder: PropTypes.number,
      precedingWait: PropTypes.object,
      completed: PropTypes.number,
      sends: PropTypes.number,
      delivered: PropTypes.number,
      opens: PropTypes.number,
      clicks: PropTypes.number,
      qualified: PropTypes.number,
      skipped: PropTypes.number,
      skippedReasons: PropTypes.object,
      errorCount: PropTypes.number,
      errorReasons: PropTypes.object,
    })
  ),
  cadence: PropTypes.object,
  loading: PropTypes.bool,
  error: PropTypes.string,
};

export default OverallPerformanceTable;
